/**
 * アプリケーション固有のJavaScript
 *
 * このファイルに独自のプログラムを記述してください。
 * ベンダーライブラリとは分離されているため、自由に編集できます。
 *
 * 機能が増えてきた場合は modules/ フォルダに分割することを推奨します。
 * 例: import { initForm } from "./modules/form.js";
 */

// ========================================
// 定数
// ========================================

// 設定値
const ANCHOR_GAP = 20; // アンカー着地時、固定ヘッダーの下に空ける余白
const HOME_KV_ACTIVE_DELAY = 1100; // KV に is-active を付けるまでの待ち時間（load 後）
const HOME_KV_SYMBOL_DELAY = 400; // KV の is-active からシンボルに付けるまでの待ち時間
const HOME_KV_ILLUST_DELAY = 1200; // KV の is-active からイラストに付けるまでの待ち時間

// SPレイアウトの判定（SCSS の $mq-breakpoints の md: 768px と対応）
const SP_MEDIA_QUERY = "(max-width: 767px)";

// アニメーションを減らす設定（OS の「視差効果を減らす」）
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

// クラス名（状態）
const CLASS = {
  active: "is-active",
  scrolled: "is-scrolled",
  show: "is-show",
};

// セレクター（HTML側と連動）
const SELECTOR = {
  header: "header",
  loading: ".js-loading",
  menu: ".js-menu",
  menuButton: ".js-menuButton",
  menuDropdown: ".js-menuDropdown",
  scrollAnimation: ".js-scrollAnimation",
  homeKv: ".js-home-kv",
  homeKvSymbol: ".js-home-kvSymbol",
  homeKvIllust: ".js-home-kvIllust",
  anchorLink: 'a[href^="#"]',
};

// ========================================
// ユーティリティ関数
// ========================================

/**
 * アンカー着地時に差し引くヘッダーの高さを取得
 *
 * ヘッダーが fixed / sticky のときだけ、実測した高さ + 余白を返す。
 * 固定していないヘッダーはコンテンツを覆わないので 0 を返す。
 * 固定値ではなく都度実測するため、CSS 側でヘッダーの高さや固定方法を変えても追随する。
 *
 * @returns {number} 差し引く高さ(px)
 */
function getHeaderHeight() {
  const header = document.querySelector(SELECTOR.header);

  if (!header) return 0;

  const { position } = window.getComputedStyle(header);
  if (position !== "fixed" && position !== "sticky") return 0;

  return header.getBoundingClientRect().height + ANCHOR_GAP;
}

/**
 * 指定位置へスクロール
 *
 * OS で「視差効果を減らす」が有効な場合は、smooth を指定されても即時移動にする。
 *
 * @param {number} position - スクロール位置
 * @param {string} behavior - スクロール動作 ('smooth' | 'auto')
 */
function scrollToPosition(position, behavior = "smooth") {
  const reduced = window.matchMedia(REDUCED_MOTION_QUERY).matches;

  window.scroll({
    top: Math.max(0, position),
    behavior: reduced ? "auto" : behavior,
  });
}

/**
 * ハッシュから対象要素を取得する
 *
 * @param {string} hash - '#id' 形式の文字列
 * @returns {HTMLElement|null} 対象要素。プレースホルダや不在の場合は null
 */
function getAnchorTarget(hash) {
  if (!hash || hash === "#") return null;

  // 日本語 id など、エンコードされたハッシュにも対応する
  let id = "";
  try {
    id = decodeURIComponent(hash.slice(1));
  } catch {
    id = hash.slice(1); // 不正なエスケープが含まれる場合は素のまま扱う
  }

  return id ? document.getElementById(id) : null;
}

/**
 * アンカー先へフォーカスを移す
 *
 * 見出しやセクションは既定でフォーカスを受け取れないため、一時的に tabindex="-1" を付ける。
 * これが無いと、スクロールしてもフォーカスはリンク側に残るため
 * - キーボード利用者は続く Tab で「目次の次の項目」へ進んでしまう
 * - スクリーンリーダーは移動先の見出しを読み上げない
 * という状態になり、アンカーが事実上マウス専用の機能になる。
 *
 * @param {HTMLElement} target - アンカー先の要素
 */
function focusAnchorTarget(target) {
  // もともと tabindex を持つ要素（リンク・ボタン等）はそのまま使う
  if (!target.hasAttribute("tabindex")) {
    target.setAttribute("tabindex", "-1");
    // フォーカスが外れたら元に戻し、不要な属性を残さない
    target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
  }

  // スクロールは scrollToPosition が担当するので、focus では動かさない
  target.focus({ preventScroll: true });
}

/**
 * アンカー先へ移動する（スクロール + フォーカス + URL 更新）
 *
 * @param {HTMLElement} target - アンカー先の要素
 * @param {string|null} hash - 履歴に積むハッシュ（'#id'）。null なら履歴を更新しない
 * @param {string} behavior - スクロール動作 ('smooth' | 'auto')
 */
function goToAnchor(target, hash = null, behavior = "smooth") {
  const startY = window.scrollY;
  const position = startY + target.getBoundingClientRect().top - getHeaderHeight();

  // 共有・ブックマーク・ブラウザバックのために URL を更新する
  // （既定のフラグメントナビゲーションを止めている分を自前で補う）
  //
  // ※ スクロールより先に履歴を積むこと。順序を逆にすると、ブラウザが
  //   「移動後の位置」を戻り先の履歴に記録してしまい、戻っても元の位置に復帰しない
  if (hash && window.location.hash !== hash) {
    // 「戻る」で元の位置へ復帰できるよう、移動前のスクロール位置を今の履歴に残す
    window.history.replaceState({ scrollY: startY }, "");
    window.history.pushState(null, "", hash);
  }

  scrollToPosition(position, behavior);
  focusAnchorTarget(target);
}

// ========================================
// 機能別初期化関数
// ========================================

/**
 * ハンバーガーメニューの初期化
 *
 * 開閉状態を aria-expanded と inert に同期する。
 * - aria-label は付けない（ボタン内の可視テキストがアクセシブル名になる）
 * - inert は SP のドロワー表示時のみ付ける。PC はナビが常時表示なので付けない
 * - CSS 側でも閉状態に visibility: hidden / display: none を必ず含める
 */
function initHamburgerMenu() {
  const menu = document.querySelector(SELECTOR.menu);
  const menuButton = document.querySelector(SELECTOR.menuButton);

  if (!menu || !menuButton) return null;

  const spMedia = window.matchMedia(SP_MEDIA_QUERY);
  const isOpen = () => menu.classList.contains(CLASS.active);

  const syncState = () => {
    const open = isOpen();
    menuButton.setAttribute("aria-expanded", String(open));
    // 閉じている間はドロワー内をキーボード操作・支援技術から除外する
    menu.inert = spMedia.matches && !open;
  };

  const toggle = () => {
    menu.classList.toggle(CLASS.active);
    menuButton.classList.toggle(CLASS.active);
    syncState();
  };

  const close = () => {
    menu.classList.remove(CLASS.active);
    menuButton.classList.remove(CLASS.active);
    syncState();
  };

  menuButton.addEventListener("click", toggle);

  // ブレークポイントを跨いだら状態を整える（PC化したらドロワーを閉じる）
  spMedia.addEventListener("change", () => {
    if (spMedia.matches) {
      syncState();
    } else {
      close();
    }
  });

  // マークアップの初期値と実際の状態を一致させる
  syncState();

  // 外部から制御できるようにオブジェクトを返す
  return { toggle, close, isOpen };
}

/**
 * ドロップダウンメニューの初期化
 */
function initDropdownMenu() {
  const dropdowns = document.querySelectorAll(SELECTOR.menuDropdown);

  if (dropdowns.length === 0) return;

  dropdowns.forEach((button) => {
    button.addEventListener("click", () => {
      button.classList.toggle(CLASS.active);
    });
  });
}

/**
 * スムーススクロールの初期化
 *
 * 「#id が指す要素が実在する場合のみスクロールする」というルールで動作する。
 * ページ内のアンカーが何本あっても、すべて同じ経路（goToAnchor）を通る。
 *
 * - `href="#"`（URL未確定のプレースホルダ）はクリックしても何も起こさない
 * - 対象 id が実在しない場合はブラウザの既定動作に任せる
 * - ページ最上部へ戻したい場合は body 等に id を付け、`href="#top"` のように
 *   実在する id を指定する（特別扱いはしない）
 * - スクロールに加えてフォーカス移動と URL 更新も行う（goToAnchor 参照）
 * - ブラウザバック / フォワードでも同じ位置に戻る（popstate）
 *
 * @param {Object|null} hamburgerMenu - ハンバーガーメニューの制御オブジェクト
 */
function initSmoothScroll(hamburgerMenu) {
  const anchors = document.querySelectorAll(SELECTOR.anchorLink);

  if (anchors.length === 0) return;

  anchors.forEach((anchor) => {
    anchor.addEventListener("click", (e) => {
      // 修飾キー付き / 中クリックは「別タブで開く」等の既定動作を尊重する
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      const href = anchor.getAttribute("href");

      // href が空 or "#" 単体はプレースホルダ扱い。何もしない
      // （ブラウザ既定の「最上部へジャンプ」も抑止する）
      if (!href || href === "#") {
        e.preventDefault();
        return;
      }

      const target = getAnchorTarget(href);

      if (!target) return; // 対象が存在しない場合はデフォルト動作

      e.preventDefault();

      // メニューが開いていれば閉じる
      if (hamburgerMenu?.isOpen()) hamburgerMenu.close();

      goToAnchor(target, href);
    });
  });

  // ブラウザバック / フォワードでもアンカー位置へ戻す
  // （pushState で履歴を積んでいるため、既定のジャンプは起きない）
  window.addEventListener("popstate", (e) => {
    const target = getAnchorTarget(window.location.hash);

    if (target) {
      goToAnchor(target, null, "auto");
      return;
    }

    // ハッシュの無い履歴へ戻った場合は、記録しておいた移動前の位置へ復帰する
    if (typeof e.state?.scrollY === "number") scrollToPosition(e.state.scrollY, "auto");
  });
}

/**
 * ページ遷移時のアンカー位置調整
 *
 * 他ページから `/privacy-policy/#cookie` のように直接来た場合、
 * ブラウザは固定ヘッダーを考慮せずにジャンプする。位置を測り直して着地させ、
 * あわせてフォーカスも移すことで、支援技術の利用者にも移動先が伝わるようにする。
 */
function initAnchorPositionAdjust() {
  window.addEventListener("load", () => {
    const target = getAnchorTarget(window.location.hash);

    if (!target) return;

    goToAnchor(target, null, "auto");
  });
}

/**
 * スクロールアニメーションの初期化（Intersection Observer）
 */
function initScrollAnimation() {
  const items = document.querySelectorAll(SELECTOR.scrollAnimation);

  if (items.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(CLASS.scrolled);
          // 一度アニメーションしたら監視を解除（パフォーマンス向上）
          observer.unobserve(entry.target);
        }
      });
    },
    {
      root: null,
      rootMargin: "60% 0px -40% 0px",
      threshold: 0,
    },
  );

  items.forEach((item) => observer.observe(item));
}

/**
 * トップ KV の初期化
 *
 * ページ読み込み完了（load）から 1.5 秒後に KV へ is-active を付ける。
 * 続けて、旧 CSS の transition-delay 相当だけずらして
 * シンボル（0.4s）・イラスト（1.2s）にも is-active を付ける。
 * 対象が無いページでは何もしない。
 */
function initHomeKv() {
  const kv = document.querySelector(SELECTOR.homeKv);

  if (!kv) return;

  const symbols = kv.querySelectorAll(SELECTOR.homeKvSymbol);
  const illust = kv.querySelector(SELECTOR.homeKvIllust);

  window.addEventListener("load", () => {
    window.setTimeout(() => {
      kv.classList.add(CLASS.active);

      window.setTimeout(() => {
        symbols.forEach((symbol) => symbol.classList.add(CLASS.active));
      }, HOME_KV_SYMBOL_DELAY);

      if (illust) {
        window.setTimeout(() => {
          illust.classList.add(CLASS.active);
        }, HOME_KV_ILLUST_DELAY);
      }
    }, HOME_KV_ACTIVE_DELAY);
  });
}

// ========================================
// メイン初期化関数
// ========================================

/**
 * アプリケーション初期化
 */
export function initApp() {
  // ハンバーガーメニュー
  const hamburgerMenu = initHamburgerMenu();

  // ドロップダウンメニュー
  initDropdownMenu();

  // スムーススクロール（ハンバーガーメニューと連携）
  initSmoothScroll(hamburgerMenu);

  // ページ遷移時のアンカー位置調整
  initAnchorPositionAdjust();

  // スクロールアニメーション
  initScrollAnimation();

  // トップ KV
  initHomeKv();

  // ページ読み込み完了後にis-showクラスを追加
  const loading = document.querySelector(SELECTOR.loading);
  if (loading) {
    window.addEventListener("load", () => {
      loading.classList.add(CLASS.show);
    });
  }

  // ========================================
  // 追加の初期化処理はここに記述
  // ========================================

  // 例: 外部モジュールの読み込み
  // import("./modules/form.js").then(({ initForm }) => {
  //   initForm();
  // });
}
